import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatIndiaTime } from '../config/india-timezone';
import {
  Building2,
  Crown,
  Shield,
  User,
  Check,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Search,
  ChevronUp,
  ChevronDown
} from 'lucide-react';

const WorkspaceSelection = () => {
  const {
    workspaces,
    currentWorkspace,
    switchWorkspace,
    loading,
    subscriptionLimit,
    subscriptionInfo
  } = useWorkspace();

  const atLimit = subscriptionInfo && subscriptionInfo.can_create_workspace === false;

  /** Show plan upgrade only if user is owner/admin somewhere (or super admin), or list is empty (solo account / first load). */
  const hasOwnerOrAdminWorkspace = workspaces.some((w) =>
    ['owner', 'admin'].includes(String(w.user_role || '').toLowerCase())
  );
  const canManageBillingUi =
    subscriptionInfo?.is_super_admin ||
    !workspaces.length ||
    hasOwnerOrAdminWorkspace;

  const [error] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const itemsPerPage = 5;

  const filteredAndSortedWorkspaces = useMemo(() => {
    let list = workspaces;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (w) =>
          (w.name || '').toLowerCase().includes(q) ||
          (w.description || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'description':
          aVal = (a.description || '').toLowerCase();
          bVal = (b.description || '').toLowerCase();
          break;
        case 'created_at':
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        case 'total_users':
          aVal = a.member_count ?? a.total_users ?? 0;
          bVal = b.member_count ?? b.total_users ?? 0;
          break;
        case 'user_role':
          aVal = (a.user_role || '').toLowerCase();
          bVal = (b.user_role || '').toLowerCase();
          break;
        default:
          aVal = a.name || '';
          bVal = b.name || '';
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [workspaces, searchQuery, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredAndSortedWorkspaces.length / itemsPerPage) || 1;
  const paginatedWorkspaces = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedWorkspaces.slice(start, start + itemsPerPage);
  }, [filteredAndSortedWorkspaces, currentPage, itemsPerPage]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ChevronUp className="w-4 h-4 opacity-30" />;
    return sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [filteredAndSortedWorkspaces.length, currentPage, totalPages]);

  // const handleCreateNowgray = async () => {
  //   setCreatingNowgray(true);
  //   const result = await createNowgrayWorkspace();
  //   setCreatingNowgray(false);

  //   if (result.success) {
  //     toast.success('Nowgray workspace created successfully!');
  //   }
  // };

  // const handleRefresh = async () => {
  //   setRefreshing(true);
  //   setError(null);
  //   const result = await refreshWorkspaces();
  //   setRefreshing(false);

  //   if (result.success) {
  //     toast.success('Workspaces refreshed successfully!');
  //   } else {
  //     setError(result.error || 'Failed to refresh workspaces');
  //   }
  // };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-blue-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'admin':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading workspaces...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>


        </div>

        {/* Workspaces Table */}
        {workspaces.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900">Select Workspace</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search workspaces..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full sm:w-64"
                />
              </div>
            </div>
            <div className="overflow-x-auto max-h-[calc(100vh-320px)] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        Workspace
                        <SortIcon column="name" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('description')}
                    >
                      <div className="flex items-center gap-1">
                        Description
                        <SortIcon column="description" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('user_role')}
                    >
                      <div className="flex items-center gap-1">
                        Role
                        <SortIcon column="user_role" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('total_users')}
                    >
                      <div className="flex items-center gap-1">
                        Members
                        <SortIcon column="total_users" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Created
                        <SortIcon column="created_at" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Select
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedWorkspaces.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        No workspaces match your search.
                      </td>
                    </tr>
                  ) : paginatedWorkspaces.map((workspace) => (
                    <tr
                      key={workspace.id}
                      onClick={() => switchWorkspace(workspace)}
                      className={`hover:bg-blue-50 transition-colors cursor-pointer ${currentWorkspace?.id === workspace.id ? 'bg-blue-50' : ''
                        }`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                            <Building2 className="w-5 h-5 text-white" />
                          </div>
                          <span className="font-medium text-gray-900">{workspace.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                        {workspace.description || '—'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(workspace.user_role)}`}>
                          {getRoleIcon(workspace.user_role)}
                          <span className="ml-1 capitalize">{workspace.user_role}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {workspace.member_count ?? workspace.total_users ?? 0} member{(workspace.member_count ?? workspace.total_users ?? 0) !== 1 ? 's' : ''}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatIndiaTime(workspace.created_at, 'relative')}
                      </td>
                      <td className="px-6 py-4">
                        {currentWorkspace?.id === workspace.id ? (
                          <span className="inline-flex items-center text-blue-600 text-sm font-medium">
                            <Check className="w-4 h-4 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="text-sm text-blue-600 font-medium">Select</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredAndSortedWorkspaces.length)} of {filteredAndSortedWorkspaces.length} workspaces
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading workspaces</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {workspaces.length === 0 && !error && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No workspaces found</h3>
            <p className="text-gray-600 mb-6">
              {atLimit || subscriptionLimit
                ? canManageBillingUi
                  ? 'Upgrade your plan to create workspaces.'
                  : 'Your workspace limit is reached. Ask a workspace owner or admin to upgrade the plan if you need more workspaces.'
                : 'You don\'t have access to any workspaces yet. Create a new workspace or ask an admin to invite you.'}
            </p>
            {(atLimit || subscriptionLimit) && canManageBillingUi && (
              <Link
                to="/billing"
                className="inline-flex items-center px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
              >
                Upgrade plan
              </Link>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default WorkspaceSelection;
